// /oauth/consent — where an MCP client (Claude.ai, Claude Desktop, …) sends
// the browser to ask for access. Outside the AppShell like login; the router
// sends a signed-out visitor through /login and back. The API holds the
// request; this page only shows it and posts the decision.

import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ApiError, oauthApi, rateLimitMessage } from '../lib/api-client'
import { LoadingRows, Notice } from '../components/ui'
import { LogoMark } from '../components/logo'

const SCOPE_TEXT = {
  mcp: 'Read and change: use Goblic’s tools with your rights, in the workspace you pick.',
  'mcp:read': 'Read only: look around in the workspace you pick, without changing anything.',
} as const

export function OAuthConsentPage() {
  const requestId = new URLSearchParams(window.location.search).get('request') ?? ''
  const [workspaceId, setWorkspaceId] = useState('')

  const requestQuery = useQuery({
    queryKey: ['oauth-request', requestId],
    queryFn: () => oauthApi.getRequest(requestId),
    enabled: requestId !== '',
    retry: false,
  })

  const decide = useMutation({
    mutationFn: (approve: boolean) =>
      oauthApi.decide({
        requestId,
        approve,
        ...(approve ? { workspaceId } : {}),
      }),
    // Back to the app that asked: with a code when approved, an error when denied.
    onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
  })

  const request = requestQuery.data
  const workspaces = request?.workspaces ?? []
  const chosen = workspaces.find((w) => w.id === workspaceId)
  const busy = decide.isPending || decide.isSuccess

  let body
  if (requestId === '') {
    body = <Notice tone="error">This link has no request in it. Start again from the app.</Notice>
  } else if (requestQuery.isLoading) {
    body = <LoadingRows />
  } else if (requestQuery.isError) {
    const gone = requestQuery.error instanceof ApiError && requestQuery.error.status === 404
    body = (
      <Notice tone="error">
        {rateLimitMessage(requestQuery.error) ??
          (gone
          ? 'This request has expired or was already answered. Start again from the app.'
          : `Couldn’t load the request: ${(requestQuery.error as Error).message}`)}
      </Notice>
    )
  } else if (request) {
    body = (
      <>
        <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
          Connect {request.clientName}?
        </h2>
        <p className="mt-1 text-base text-ink-2">
          This app wants to use Goblic on your behalf.
        </p>

        <dl className="mt-5 space-y-3 rounded-control border border-line bg-sunken px-4 py-3 text-base">
          <div>
            <dt className="text-sm font-medium text-ink-2">App</dt>
            <dd className="text-ink">{request.clientName}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-ink-2">Returns you to</dt>
            <dd className="font-mono font-semibold text-ink">{request.redirectHost}</dd>
            <dd className="text-sm text-ink-3">
              Anyone can register an app under any name. Only approve if you
              recognise this address.
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-ink-2">Access</dt>
            <dd className="text-ink">{SCOPE_TEXT[request.scope]}</dd>
          </div>
        </dl>

        <fieldset className="mt-5">
          <legend className="text-sm font-medium text-ink-2">Workspace</legend>
          <div className="mt-2 space-y-2">
            {workspaces.length === 0 ? (
              <p className="text-base text-ink-3">You aren’t a member of any workspace.</p>
            ) : null}
            {workspaces.map((w) => (
              <label
                key={w.id}
                className={`flex items-start gap-3 rounded-control border px-3 py-2.5 ${
                  w.externalApiAccess
                    ? 'cursor-pointer border-line hover:bg-sunken'
                    : 'cursor-not-allowed border-line opacity-60'
                }`}
              >
                <input
                  type="radio"
                  name="workspace"
                  className="mt-1"
                  value={w.id}
                  checked={workspaceId === w.id}
                  disabled={!w.externalApiAccess || busy}
                  onChange={() => setWorkspaceId(w.id)}
                />
                <span>
                  <span className="block text-base font-medium text-ink">{w.name}</span>
                  {!w.externalApiAccess ? (
                    <span className="block text-sm text-ink-3">
                      External access is off. A workspace owner can turn it on
                      in Settings.
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {decide.isError ? (
          <div className="mt-4">
            <Notice tone="error">
              {rateLimitMessage(decide.error) ??
                (decide.error instanceof ApiError && decide.error.status === 404
                ? 'This request has expired. Start again from the app.'
                : decide.error instanceof ApiError && decide.error.status === 403
                  ? 'You can’t connect this workspace: you aren’t a member, or external access is off.'
                  : `Something went wrong: ${(decide.error as Error).message}`)}
            </Notice>
          </div>
        ) : null}

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            className="btn-primary h-11 flex-1 rounded-[10px] text-md"
            disabled={busy || !chosen?.externalApiAccess}
            onClick={() => decide.mutate(true)}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn-secondary h-11 flex-1 rounded-[10px] text-md"
            disabled={busy}
            onClick={() => decide.mutate(false)}
          >
            Deny
          </button>
        </div>
      </>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      <div className="relative mx-auto grid min-h-screen w-full items-center justify-items-center px-4 py-12">
        <div className="w-full max-w-[460px]">
          <div className="mb-6 flex items-center gap-3">
            <LogoMark size={44} />
            <span className="font-display text-xl font-semibold tracking-[-0.02em] text-ink">
              Goblic
            </span>
          </div>
          <div className="rounded-[20px] border border-line bg-surface/95 p-8 shadow-overlay backdrop-blur-sm">
            {body}
          </div>
        </div>
      </div>
    </div>
  )
}
